/**
 * Cambio de conceptos al marcar (o desmarcar) a un empleado como asimilado a salarios.
 *
 * Lo usan AgregarEmpleado y EditarEmpleado, que son las dos pantallas donde se captura el
 * Tipo de Régimen. Vive aquí y no dentro de cada una para que las dos hagan exactamente lo
 * mismo — mismo criterio que Pages/Panel/Conceptos/aplicarConceptoLey.js.
 *
 * Escribe sólo en el formulario: nada se guarda hasta que el usuario le da a Guardar, y la
 * lista queda visible y editable en la pestaña Conceptos antes de eso.
 *
 * El juego de conceptos NO se decide aquí: lo resuelve el backend y viaja en
 * `datosParaEmpleado` (res.data.asimilados). Si la lógica viviera en los dos lados, el
 * formulario propondría una cosa y el guardado —que valida el invariante— exigiría otra.
 */
import React from "react";
import { Modal, Typography, message } from "antd";
import { esRegimenAsimilado } from "../../../constants/nomina/tipoRegimen";

const { Text, Paragraph } = Typography;

const etiquetaConcepto = (c) => {
    if (!c) return "";
    const clave = [c.serie, c.numero].filter((x) => x !== undefined && x !== null).join("");
    return clave ? `${clave} · ${c.descripcion || ""}`.trim() : c.descripcion || "";
};

const listaDeIds = (ids, porId) =>
    (ids || []).map((id) => porId.get(String(id))).filter(Boolean);

const BloqueConceptos = ({ titulo, conceptos, color }) => {
    if (!conceptos.length) return null;
    return (
        <div style={{ marginBottom: 10 }}>
            <Text strong style={{ fontSize: 12, color }}>
                {titulo} ({conceptos.length})
            </Text>
            <div
                style={{
                    maxHeight: 140,
                    overflowY: "auto",
                    border: "1px solid #f0f0f0",
                    borderRadius: 4,
                    padding: 8,
                    marginTop: 4,
                    fontSize: 12,
                }}
            >
                {conceptos.map((c) => (
                    <div key={c._id}>{etiquetaConcepto(c)}</div>
                ))}
            </div>
        </div>
    );
};

const confirmar = ({ titulo, contenido, okText }) =>
    new Promise((resolve) => {
        Modal.confirm({
            title: titulo,
            width: 620,
            okText,
            cancelText: "Cancelar",
            content: contenido,
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

/**
 * ¿La clave de régimen de este _id es de asimilados?
 *
 * @param tipoRegimenId  el _id seleccionado en el form
 * @param tiposDeRegimenes  el arreglo crudo del catálogo (con c_TipoRegimen)
 */
export const regimenIdEsAsimilado = (tipoRegimenId, tiposDeRegimenes = []) => {
    if (!tipoRegimenId) return false;
    const doc = tiposDeRegimenes.find((r) => String(r._id) === String(tipoRegimenId));
    return !!doc && esRegimenAsimilado(doc.c_TipoRegimen);
};

/**
 * ¿El check "incluir en cada nómina" está prendido?
 *
 * Es DERIVADO: no hay campo en la base. El check está prendido si y sólo si la percepción
 * de asimilados está dentro de conceptosIds, que es exactamente el mismo hecho. Un booleano
 * aparte sólo podría desincronizarse con la lista que de verdad manda.
 */
export const percepcionIncluida = (conceptosIds, percepcionId) =>
    !!percepcionId && (conceptosIds || []).some((id) => String(id) === String(percepcionId));

/**
 * Agrega o quita la percepción de asimilados de la lista, conservando el resto.
 * Devuelve la lista nueva.
 */
export const alternarPercepcion = (conceptosIds, percepcionId, incluir) => {
    const actuales = (conceptosIds || []).filter((id) => String(id) !== String(percepcionId));
    if (!incluir || !percepcionId) return actuales;
    // Al frente: es la percepción, y así se lee primero en la tabla.
    return [percepcionId, ...actuales];
};

/**
 * Cambio de régimen. Propone la lista nueva, la enseña y —si el usuario acepta— la escribe
 * en el formulario junto con los campos que dejan de tener sentido.
 *
 * @returns {Promise<boolean>} true si se aplicó; false si el usuario canceló (y entonces
 *                             quien llama debe revertir el select a su valor anterior).
 */
export async function aplicarCambioDeRegimen({
    formRef,
    esAsimiladoNuevo,
    asimilados,          // res.data.asimilados de datosParaEmpleado
    todasConceptos,
    conceptosGlobalesIds, // el juego normal, para cuando se SALE del régimen asimilado
}) {
    const form = formRef && formRef.current;
    if (!form) return false;

    const porId = new Map((todasConceptos || []).map((c) => [String(c._id), c]));
    const actuales = form.getFieldValue("conceptosIds") || [];

    if (esAsimiladoNuevo) {
        // Sin los conceptos de ley en el catálogo no se puede armar el juego. Se dice qué
        // falta en vez de dejar al empleado a medio configurar (el guardado lo rechazaría).
        if (asimilados?.faltantes?.length) {
            Modal.error({
                title: "Faltan conceptos de nómina",
                width: 620,
                content: (
                    <div>
                        <Paragraph style={{ marginBottom: 8 }}>
                            Para configurar a un empleado asimilado a salarios hacen falta estos
                            conceptos, y tu catálogo no los tiene:
                        </Paragraph>
                        <ul style={{ paddingLeft: 18, fontSize: 13 }}>
                            {asimilados.faltantes.map((f) => (
                                <li key={f}>{f}</li>
                            ))}
                        </ul>
                        <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                            Créalos desde Configuración de Nómina → asistente → “Agregar conceptos
                            de ley”, y vuelve a elegir el régimen.
                        </Paragraph>
                    </div>
                ),
            });
            return false;
        }

        const nuevos = asimilados?.conceptosIds || [];
        const setNuevos = new Set(nuevos.map(String));
        const setActuales = new Set(actuales.map(String));

        const seQuitan = listaDeIds(actuales.filter((id) => !setNuevos.has(String(id))), porId);
        const sePonen = listaDeIds(nuevos.filter((id) => !setActuales.has(String(id))), porId);

        const ok = await confirmar({
            titulo: "Cambiar a régimen de asimilados a salarios",
            okText: "Aplicar",
            contenido: (
                <div>
                    <Paragraph style={{ marginBottom: 10 }}>
                        Un asimilado a salarios no tiene relación laboral: no le corresponden
                        Sueldo, Séptimo Día, cuotas del IMSS ni subsidio al empleo, y se le retiene
                        el ISR completo. Se va a ajustar su lista de conceptos:
                    </Paragraph>
                    <BloqueConceptos titulo="Se quitan" conceptos={seQuitan} color="#a8071a" />
                    <BloqueConceptos titulo="Se ponen" conceptos={sePonen} color="#135200" />
                    <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                        También se apagan el cálculo de PTU y el de aguinaldo, que son prestaciones
                        de la LFT. Revisa además el tipo de contrato: el SAT sólo acepta las claves
                        09, 10 y 99 con este régimen. Puedes ajustar la lista en la pestaña
                        Conceptos; nada se guarda hasta que le des a Guardar.
                    </Paragraph>
                </div>
            ),
        });
        if (!ok) return false;

        form.setFieldsValue({
            conceptosIds: nuevos,
            calculoPTU: false,
            calculoAguinaldo: false,
        });
        message.success(
            "Se ajustaron los conceptos para el régimen de asimilados a salarios. Revísalos en la pestaña Conceptos.",
            6
        );
        return true;
    }

    // ── Sale del régimen de asimilados ──────────────────────────────────────
    const nuevos = conceptosGlobalesIds || [];
    const setNuevos = new Set(nuevos.map(String));
    const setActuales = new Set(actuales.map(String));
    const seQuitan = listaDeIds(actuales.filter((id) => !setNuevos.has(String(id))), porId);
    const sePonen = listaDeIds(nuevos.filter((id) => !setActuales.has(String(id))), porId);

    if (!seQuitan.length && !sePonen.length) return true;

    const ok = await confirmar({
        titulo: "Dejar el régimen de asimilados a salarios",
        okText: "Aplicar",
        contenido: (
            <div>
                <Paragraph style={{ marginBottom: 10 }}>
                    Este empleado deja de ser asimilado, así que se le devuelven los conceptos
                    normales de la empresa:
                </Paragraph>
                <BloqueConceptos titulo="Se quitan" conceptos={seQuitan} color="#a8071a" />
                <BloqueConceptos titulo="Se ponen" conceptos={sePonen} color="#135200" />
                <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                    Revisa el cálculo de PTU y aguinaldo, y el tipo de contrato. Nada se guarda
                    hasta que le des a Guardar.
                </Paragraph>
            </div>
        ),
    });
    if (!ok) return false;

    form.setFieldsValue({ conceptosIds: nuevos });
    message.success("Se devolvieron los conceptos normales de la empresa.", 5);
    return true;
}
